// The people who take a booked call, keyed by their Cal username. The
// host Cal reports on a booking resolves to one of these when known; a
// host Cal names that the roster doesn't carry is shown by name alone,
// and no host at all shows the fallback rep.

import type { ScheduledCallHost } from "./api";
import { marketReview } from "./market-reviews";

export interface CallRep {
  name: string;
  /** A `/public/` path, or null for an initials avatar. */
  photo: string | null;
  /** The rep's own customer review; null shows the market's. */
  review: { quote: string; author: string } | null;
}

const ROSTER: Record<string, CallRep> = {
  colecm46: {
    name: "Cole Christensen",
    photo: "/public/rep-cole-christensen.webp",
    review: {
      quote:
        "Cole understood my concerns told me my options and didn't pressure me.",
      author: "Dennis W. · Trustpilot",
    },
  },
  nic001: {
    name: "Nic Call",
    photo: "/public/rep-nic-call.webp",
    review: {
      quote:
        "Nic was very helpful. Answered all my questions to my satisfaction.",
      author: "Gail N. · Google",
    },
  },
  "ethan.call": {
    name: "Ethan Call",
    photo: null,
    review: {
      quote:
        "Ethan gave such a great description of the AFTER plan that I completely changed my opinion.",
      author: "Deborah K. · Trustpilot",
    },
  },
  nathan55: {
    name: "Nathan McPherson",
    photo: null,
    review: {
      quote:
        "I was apprehensive about planning all this but Nathan was very helpful and guided me through the many questions I had.",
      author: "Linda W. · Google",
    },
  },
  "jake.hughes": {
    name: "Jake Hughes",
    photo: null,
    review: {
      quote:
        "Very friendly and informative… Excellent job keep up the great work Jake.",
      author: "Don S. · Google",
    },
  },
};

const FALLBACK_REP = ROSTER.colecm46;

/** The rep a booked call shows: the roster entry for the host's Cal
 * username, else the host by name alone (no photo, no rep review), else
 * the fallback rep. */
export function resolveCallRep(
  host: ScheduledCallHost | null | undefined,
): CallRep {
  const username = host?.username?.trim().toLowerCase();
  if (username && Object.hasOwn(ROSTER, username)) return ROSTER[username];
  const name = host?.name?.trim();
  if (name) return { name, photo: null, review: null };
  return FALLBACK_REP;
}

export function repFirstName(rep: CallRep): string {
  return rep.name.split(/\s+/)[0];
}

export interface ReviewFace {
  quote: string;
  author: string;
  /** The first name to set apart inside the quote; null when the review
   * is not about this rep. */
  highlight: string | null;
}

/** The review shown under the rep card: the rep's own, else the market's
 * localized review. */
export function reviewForRep(
  rep: CallRep,
  marketSlug: string | null,
): ReviewFace {
  if (rep.review) {
    return { ...rep.review, highlight: repFirstName(rep) };
  }
  const review = marketReview(marketSlug);
  return {
    quote: review.blurb,
    author: `${review.reviewer} · ${review.where}`,
    highlight: null,
  };
}

export interface QuoteSegment {
  text: string;
  highlight: boolean;
}

/** Splits a quote so every whole-word, case-insensitive occurrence of
 * `name` stands as its own highlighted segment. */
export function highlightSegments(
  quote: string,
  name: string | null,
): QuoteSegment[] {
  if (!name) return [{ text: quote, highlight: false }];
  const wanted = name.toLowerCase();
  const segments: QuoteSegment[] = [];
  for (const piece of quote.split(/(\p{L}[\p{L}\p{N}]*)/u)) {
    if (piece === "") continue;
    const highlight = piece.toLowerCase() === wanted;
    const last = segments[segments.length - 1];
    if (last && last.highlight === highlight) last.text += piece;
    else segments.push({ text: piece, highlight });
  }
  return segments;
}
